import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSwipeable } from "react-swipeable";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ChevronLeft, ChevronRight, CheckCircle2, Circle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { RoadmapDaySelector } from "@/components/RoadmapDaySelector";
import { useRoadmapProgress } from "@/hooks/useRoadmapProgress";
import { AuthModal } from "@/components/AuthModal";
import { StreakBadges } from "@/components/StreakBadges";
import { DailyNotes } from "@/components/DailyNotes";
import { TimeTracker } from "@/components/TimeTracker";
import {
  RoadmapWeek,
  getDayByNumber,
  getAdjacentDays,
  getTotalDays,
  getDayKey,
  normalizeRoadmapTo28Days,
} from "@/lib/roadmapHelpers";
import { calculateStreak } from "@/lib/streakHelpers";
import { cn } from "@/lib/utils";
import confetti from "canvas-confetti";
import { toast as sonnerToast } from "sonner";

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
}

const RoadmapDayPage = () => {
  const { slug, dayNumber } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [roadmap, setRoadmap] = useState<RoadmapWeek[]>([]);
  const [roadmapId, setRoadmapId] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  const currentDayNumber = parseInt(dayNumber || "1", 10);
  const totalDays = getTotalDays(roadmap);
  const dayData = getDayByNumber(roadmap, currentDayNumber);
  const { previous, next } = getAdjacentDays(currentDayNumber, totalDays);

  const { completedTasks, isDayCompleted, toggleDay, progressPercentage } = useRoadmapProgress({
    roadmapId,
    user,
    totalDays,
  });

  // Calculate streak
  const streakData = useMemo(() => {
    return calculateStreak(completedTasks, totalDays);
  }, [completedTasks, totalDays]);

  useEffect(() => {
    const initAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);

      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        setUser(session?.user ?? null);
      });

      return () => subscription.unsubscribe();
    };

    initAuth();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;

      try {
        setLoading(true);

        // Fetch blog post
        const { data: postData, error: postError } = await supabase
          .from("blog_posts")
          .select("id, title, slug")
          .eq("slug", slug)
          .eq("status", "published")
          .single();

        if (postError) throw postError;
        setPost(postData);

        // Fetch roadmap
        const { data: roadmapData, error: roadmapError } = await supabase
          .from("roadmaps")
          .select("id, content")
          .eq("blog_post_id", postData.id)
          .maybeSingle();

        if (roadmapError) throw roadmapError;

        if (roadmapData) {
          setRoadmapId(roadmapData.id);
          const rawRoadmap = roadmapData.content as unknown as RoadmapWeek[];
          // Normalize to exactly 28 days with sequential numbering (1-28)
          const normalizedRoadmap = normalizeRoadmapTo28Days(rawRoadmap);
          setRoadmap(normalizedRoadmap);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load roadmap",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, toast]);

  const handleToggleDay = () => {
    if (!user) {
      setAuthModalOpen(true);
      return;
    }

    if (dayData) {
      const dayKey = getDayKey(dayData.week, dayData.day);
      const wasCompleted = isDayCompleted(dayKey);
      toggleDay(dayKey);

      // Celebrate new streak milestones
      if (!wasCompleted) {
        const newStreak = streakData.currentStreak + 1;
        
        // Check for badge unlocks
        if (newStreak === 7) {
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
          });
          sonnerToast.success("🔥 Week Warrior Badge Unlocked!", {
            description: "You've completed 7 days in a row!",
          });
        } else if (newStreak === 30) {
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 },
          });
          sonnerToast.success("💪 Month Master Badge Unlocked!", {
            description: "30 consecutive days completed!",
          });
        } else if (newStreak === 28) {
          confetti({
            particleCount: 200,
            spread: 160,
            origin: { y: 0.6 },
          });
          sonnerToast.success("🏆 Legendary Learner Badge Unlocked!", {
            description: "You've completed the entire 28-day journey!",
          });
        }
      }
    }
  };

  const handleNavigate = (newDayNumber: number) => {
    navigate(`/blog/${slug}/roadmap/day/${newDayNumber}`);
  };

  // Swipe handlers for mobile navigation
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (next) handleNavigate(next);
    },
    onSwipedRight: () => {
      if (previous) handleNavigate(previous);
    },
    trackMouse: false, // Only track touch, not mouse
    preventScrollOnSwipe: true,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <p className="text-muted-foreground">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!dayData) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <p className="text-destructive">Day not found</p>
          <Button asChild className="mt-4">
            <Link to={`/blog/${slug}/roadmap`}>Back to Roadmap</Link>
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  const dayKey = getDayKey(dayData.week, dayData.day);
  const isCompleted = isDayCompleted(dayKey);
  const completedCount = Object.values(completedTasks).filter(Boolean).length;

  const pageTitle = dayData && post
    ? `Day ${currentDayNumber}: ${dayData.title} - ${post.title}`
    : `Day ${currentDayNumber} - Loading...`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={`Day ${currentDayNumber} of the 28-day learning roadmap`} />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Navigation />

        <div {...swipeHandlers} className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
            <Link to={`/blog/${slug}`} className="hover:text-foreground transition-colors">
              {post?.title}
            </Link>
            <span>/</span>
            <Link to={`/blog/${slug}/roadmap`} className="hover:text-foreground transition-colors">
              Roadmap
            </Link>
            <span>/</span>
            <span className="text-foreground">Day {currentDayNumber}</span>
          </div>

          {/* Progress Bar */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Overall Progress</span>
                <span className="text-sm text-muted-foreground">{progressPercentage}%</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
              <p className="text-xs text-muted-foreground mt-2">
                Day {currentDayNumber} of {totalDays}
              </p>
            </CardContent>
          </Card>

          {/* Streak Display */}
          {user && streakData.currentStreak > 0 && (
            <div className="mb-6">
              <StreakBadges
                currentStreak={streakData.currentStreak}
                longestStreak={streakData.longestStreak}
              />
            </div>
          )}

          {/* Time Tracker */}
          {user && completedCount > 0 && (
            <div className="mb-6">
              <TimeTracker
                roadmap={roadmap}
                completedTasks={completedTasks}
              />
            </div>
          )}

          {/* Day Selector */}
          <div className="mb-6">
            <RoadmapDaySelector
              roadmap={roadmap}
              currentDayNumber={currentDayNumber}
              onDaySelect={handleNavigate}
              isDayCompleted={isDayCompleted}
            />
          </div>

          {/* Day Content */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Badge variant="outline" className="mb-2">
                    Week {dayData.week}: {dayData.weekTitle}
                  </Badge>
                  <CardTitle className={cn(
                    "text-2xl md:text-3xl",
                    isCompleted && "line-through text-muted-foreground"
                  )}>
                    Day {currentDayNumber}: {dayData.title}
                  </CardTitle>
                </div>
                {isCompleted && (
                  <CheckCircle2 className="h-8 w-8 text-green-500 shrink-0" />
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Mark as Complete */}
              <div className="flex items-center gap-3 p-4 rounded-lg border bg-card">
                {user ? (
                  <>
                    <Checkbox
                      id="day-complete"
                      checked={isCompleted}
                      onCheckedChange={handleToggleDay}
                      className="h-5 w-5"
                    />
                    <label
                      htmlFor="day-complete"
                      className="text-sm font-medium cursor-pointer flex-1"
                    >
                      {isCompleted ? "Day completed! 🎉" : "Mark this day as complete"}
                    </label>
                  </>
                ) : (
                  <>
                    <Circle className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground flex-1">
                      Sign in to track your progress
                    </span>
                    <Button size="sm" onClick={() => setAuthModalOpen(true)}>
                      Sign In
                    </Button>
                  </>
                )}
              </div>

              {/* Tasks */}
              <div>
                <h3 className="font-semibold text-lg mb-3">Today's Tasks</h3>
                <ul className={cn(
                  "space-y-2 ml-6",
                  isCompleted && "text-muted-foreground"
                )}>
                  {dayData.tasks.map((task, idx) => (
                    <li key={idx} className="list-disc">
                      {task}
                    </li>
                  ))}
                </ul>
              </div>
            </CardContent>
        </Card>

          {/* Daily Notes */}
          <DailyNotes
            user={user}
            roadmapId={roadmapId}
            dayNumber={currentDayNumber}
            onSignInRequired={() => setAuthModalOpen(true)}
            className="mb-6"
          />

          {/* Time Tracker - Only show if user is signed in */}
          {user && (
            <TimeTracker
              roadmap={roadmap}
              completedTasks={completedTasks}
              className="mb-6"
            />
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-4">
            <Button
              variant="outline"
              onClick={() => previous && handleNavigate(previous)}
              disabled={!previous}
              className="flex-1"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Previous Day
            </Button>

            <Button asChild variant="ghost">
              <Link to={`/blog/${slug}/roadmap`}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Overview
              </Link>
            </Button>

            <Button
              onClick={() => next && handleNavigate(next)}
              disabled={!next}
              className="flex-1"
            >
              Next Day
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        <Footer />
      </div>

      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
    </>
  );
};

export default RoadmapDayPage;
